import { useEffect, useRef, useState, useCallback } from 'react';
import { useIntl } from 'react-intl';
import { resolveLocale } from '../../localization/intl.ts';

import { authClient } from '../lib/auth-client.ts';
import {
  ApiResponseError,
  deckPath,
  getDeckPage,
  joinDeck,
  uploadDeckPdfFilesSequential,
  type SequentialUploadUpdate,
} from '../lib/decks.ts';
import { NotFoundContent } from '../components/NotFoundContent.tsx';
import { shouldClearFileDragging } from '../lib/deck-upload-drop.ts';
import { DeckPageAction } from '../components/DeckPageAction.tsx';
import { DeckPageMemberLayout } from '../components/DeckPageMemberLayout.tsx';
import { useServerResource } from '../lib/use-server-resource.ts';
import {
  completeDeckJoin,
  applyDeckUnlockProgress,
  type DeckHierarchy,
  deriveDeckHistoryStats,
  uploadErrorMessage,
  type DeckUploadItem,
  type UploadMessageId,
  scopeDeckHistory,
} from '../lib/deck-page.ts';
import { useReviewEventStream } from '../lib/use-review-event-stream.ts';
import { Button } from '../components/ui/button.tsx';
import { Alert, AlertDescription } from '../components/ui/alert.tsx';
import type { DeckScope } from '../../shared/deck.ts';
import { deckScopeKey } from '../../shared/deck.ts';

type Session = {
  data: { user: { id: string; name: string | null } } | null;
  refetch: () => Promise<unknown>;
};

type Props = {
  onHierarchyChange?: (hierarchy: DeckHierarchy | null) => void;
  scope?: DeckScope;
  slug: string;
  session: Session;
};

const EMPTY_DECK_SCOPE: DeckScope = {};

export function DeckPage({
  onHierarchyChange,
  scope = EMPTY_DECK_SCOPE,
  slug,
  session,
}: Props) {
  const intl = useIntl();
  const scopeKey = deckScopeKey(scope);
  const fetchPage = useCallback(
    (signal: AbortSignal) => getDeckPage(slug, fetch, signal, scope),
    [scopeKey, slug],
  );
  const deckResource = useServerResource(
    `${slug}:${scopeKey}:${session.data?.user.id ?? 'anonymous'}`,
    fetchPage,
    0,
  );
  const page = deckResource.data;
  useEffect(() => {
    onHierarchyChange?.(
      page?.isMember
        ? {
            sections: page.member.history.sections,
            topics: page.member.history.topics,
            units: page.member.history.units,
          }
        : null,
    );
  }, [onHierarchyChange, page]);
  const fetchHistorySnapshot = useCallback(async () => {
    const next = await getDeckPage(slug, fetch, undefined, scope);
    if (!next.isMember) throw new Error('Deck history is unavailable');
    return next.member.history;
  }, [scopeKey, slug]);
  const reviewStream = useReviewEventStream(
    page?.isMember ? page.member?.deckId : undefined,
    page?.isMember ? page.member?.history : undefined,
    fetchHistorySnapshot,
  );
  // The server remains authoritative for study authorization. The client
  // reuses the same pure derivation for the live map as SSE appends events.
  const liveHistory =
    page?.isMember && reviewStream.history
      ? applyDeckUnlockProgress(reviewStream.history)
      : undefined;
  const scopedHistory =
    page?.isMember && liveHistory
      ? scopeDeckHistory(liveHistory, scope)
      : undefined;
  const renderedPage =
    page?.isMember && page.member && reviewStream.history
      ? {
          ...page,
          member: {
            ...page.member,
            ...deriveDeckHistoryStats(scopedHistory ?? reviewStream.history),
            documents: Object.keys(scope).length ? [] : page.member.documents,
            history: scopedHistory ?? reviewStream.history,
          },
        }
      : page;
  const [joining, setJoining] = useState(false);
  const [joinError, setJoinError] = useState(false);
  const [dragging, setDragging] = useState(false);
  const [uploadItem, setUploadItem] = useState<DeckUploadItem | null>(null);

  useEffect(() => {
    if (!page || !page.isMember || Object.keys(scope).length > 0) return;
    const requestedDocumentId = new URL(window.location.href).searchParams.get(
      'document',
    );
    if (!requestedDocumentId) return;
    const requested = document.getElementById(
      `reference-document-${requestedDocumentId}`,
    );
    requested?.scrollIntoView({ block: 'center' });
    requested?.focus({ preventScroll: true });
  }, [page, scopeKey]);

  useEffect(() => {
    const isMemberPage =
      Boolean(page?.isMember) && Object.keys(scope).length === 0;
    if (!isMemberPage) return;
    const preventFileNavigation = (event: globalThis.DragEvent) => {
      if (!event.dataTransfer?.types.includes('Files')) return;
      event.preventDefault();
      setDragging(event.type !== 'drop');
      if (event.type === 'drop') {
        setDragging(false);
        enqueueUploadFiles(Array.from(event.dataTransfer.files));
      }
    };
    const clearDraggingOnWindowLeave = (event: globalThis.DragEvent) => {
      if (!event.dataTransfer?.types.includes('Files')) return;
      if (
        shouldClearFileDragging(
          event.relatedTarget,
          (target) =>
            target instanceof Node && document.documentElement.contains(target),
        )
      )
        setDragging(false);
    };
    window.addEventListener('dragover', preventFileNavigation);
    window.addEventListener('drop', preventFileNavigation);
    window.addEventListener('dragleave', clearDraggingOnWindowLeave);
    return () => {
      window.removeEventListener('dragover', preventFileNavigation);
      window.removeEventListener('drop', preventFileNavigation);
      window.removeEventListener('dragleave', clearDraggingOnWindowLeave);
    };
  }, [page, scopeKey]);

  const uploadQueueRef = useRef<File[]>([]);
  const uploadRunningRef = useRef(false);

  async function drainUploadQueue() {
    if (uploadRunningRef.current) return;
    uploadRunningRef.current = true;
    try {
      while (uploadQueueRef.current.length > 0) {
        const files = uploadQueueRef.current.splice(0);
        const currentPage = page;
        if (!currentPage || !currentPage.isMember || !currentPage.member) break;
        await uploadDeckPdfFilesSequential(
          currentPage.member.deckId,
          files,
          undefined,
          async (update: SequentialUploadUpdate) => {
            if (update.status === 'uploading') {
              setUploadItem({
                name: update.file.name,
                status: 'uploading',
              });
              return;
            }
            const messageId: UploadMessageId =
              update.status === 'success'
                ? 'web.deckPage.uploadSuccess'
                : update.status === 'unsupported'
                  ? 'web.deckPage.uploadUnsupported'
                  : uploadErrorMessage(update.error);
            setUploadItem({
              messageId,
              name: update.file.name,
              status: 'result',
            });
            if (update.status === 'success') deckResource.refresh();
          },
        );
      }
    } finally {
      uploadRunningRef.current = false;
      if (uploadQueueRef.current.length > 0) void drainUploadQueue();
    }
  }

  function enqueueUploadFiles(files: File[]) {
    if (!page || !page.isMember || !page.member) return;
    uploadQueueRef.current.push(...files);
    void drainUploadQueue();
  }

  async function join(signUp = false) {
    setJoining(true);
    setJoinError(false);
    try {
      await completeDeckJoin({
        auth: {
          signIn: authClient.signIn.passkey,
          signUp: authClient.signUp.passkey,
        },
        join: joinDeck,
        locale: resolveLocale(navigator.languages[0] ?? navigator.language),
        navigate: (deckSlug) => {
          window.location.assign(deckPath(deckSlug, scope));
        },
        refetch: session.refetch,
        signUp,
        signedIn: Boolean(session.data),
        slug,
      });
    } catch {
      setJoinError(true);
    } finally {
      setJoining(false);
    }
  }

  if (
    deckResource.error instanceof ApiResponseError &&
    deckResource.error.status === 404
  )
    return <NotFoundContent />;
  if (deckResource.error && !page)
    return (
      <>
        <Alert variant="destructive">
          <AlertDescription>
            {intl.formatMessage({ id: 'web.decks.error' })}
          </AlertDescription>
        </Alert>
        <Button
          className="mt-3"
          onClick={deckResource.refresh}
          type="button"
          variant="secondary"
        >
          {intl.formatMessage({ id: 'web.deckHistory.retry' })}
        </Button>
      </>
    );
  if (!renderedPage)
    return (
      <>
        <p className="text-muted-foreground">
          {intl.formatMessage({ id: 'web.decks.loading' })}
        </p>
      </>
    );
  if (!renderedPage.isMember)
    return (
      <>
        <DeckPageAction
          page={renderedPage}
          joining={joining}
          joinError={joinError}
          onJoin={(signUp) => void join(signUp)}
          signedIn={Boolean(session.data)}
        />
      </>
    );
  if (!renderedPage.member)
    return (
      <>
        <Alert variant="destructive">
          <AlertDescription>
            {intl.formatMessage({ id: 'web.decks.error' })}
          </AlertDescription>
        </Alert>
      </>
    );
  return (
    <DeckPageMemberLayout
      dragging={dragging}
      onDraggingChange={setDragging}
      onFilesSelected={(files) => {
        setDragging(false);
        enqueueUploadFiles(files);
      }}
      page={{ ...renderedPage, isMember: true, member: renderedPage.member }}
      scope={scope}
      uploadItem={uploadItem}
      isRefreshing={deckResource.isRefreshing}
      onRetry={deckResource.refresh}
      refreshError={reviewStream.error ?? deckResource.error}
    />
  );
}
